import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from 'components/icons';
import Skeleton from '../skeleton/Skeleton';
import GameCard from '../gameCard/gameCard';
import gameCardStyles from '../gameCard/gameCard.module.scss';
import styles from './spinDetails.module.scss';
import { formatLocalTime } from 'utils/formatDate';
import cx from 'classnames';

interface FreeChipDetailsProps {
  details: any;
  onBack: () => void;
  onPlayClick?: () => void;
  isLoading?: boolean;
  title?: string | null;
}

const FreeChipDetails: React.FC<FreeChipDetailsProps> = ({
  details,
  title,
  onBack,
  onPlayClick,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [isFadingOut, setIsFadingOut] = React.useState(false);

  const handleBack = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      onBack();
    }, 200);
  };

  const dummyGame = details
    ? {
        id: details.gameId,
        name: details.gameTitle,
        imgUrl: details.imageUrl,
        playingCount: 0,
        isFavorite: false,
      }
    : null;

  return (
    <div className={cx(styles.container, { [styles.fadingOut]: isFadingOut })}>
      <button className={styles.backButton} onClick={handleBack} aria-label="Go back">
        <ChevronLeftIcon width="20" height="20" />
      </button>

      <div className={styles.header}>
        <h2>{isLoading || !details ? <Skeleton width={180} height={24} /> : details.gameTitle}</h2>
        <p>{title || 'Free Chip Granted'}</p>
      </div>

      <div className={styles.cardWrapper}>
        {isLoading || !dummyGame ? (
          <div className={gameCardStyles.card}>
            <div className={gameCardStyles.imageContainer}>
              <Skeleton
                className={gameCardStyles.imageSkeleton}
                variant="rectangular"
                animation="shimmer"
              />
            </div>
          </div>
        ) : (
          <GameCard
            game={dummyGame}
            hideFooter={true}
            hideOverlayTop={true}
            onPlayClick={onPlayClick}
          />
        )}
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={30} height={28} />
          ) : (
            <span className={styles.statValue}>{details.chipCount}</span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.chips', 'Chips')}</span>
        </div>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={20} height={28} />
          ) : (
            <span className={styles.statValue}>{details.chipValue}</span>
          )}
          <span className={styles.statLabel}>
            {t('notifications.labels.chipValue', 'Chip Value')}
          </span>
        </div>
        <div className={styles.statCard}>
          {isLoading || !details ? (
            <Skeleton width={40} height={28} />
          ) : (
            <span className={styles.statValue}>{details.currencyCode}</span>
          )}
          <span className={styles.statLabel}>{t('notifications.labels.currency', 'Currency')}</span>
        </div>
      </div>

      <div className={styles.validUntil}>
        {isLoading || !details ? (
          <Skeleton width={160} height={16} />
        ) : (
          <span>
            {t('notifications.labels.validUntil', {
              date: formatLocalTime(details.validUntil, false),
            })}
          </span>
        )}
      </div>
    </div>
  );
};

export default FreeChipDetails;
